import {disableFastClick} from "@/shared/scripts/global.js";
import Snackbar from "../components/FlashMessages.ts";
import {NetworkRequest} from "@/backoffice/components/fetch.ts";

/*
|--------------------------------------------------------------------------
| Global variables
|--------------------------------------------------------------------------
*/

window.animSpeed = 200;
window.animSpeedFast = 100;
window.popupTimeout = 5000;

window.lang = document.body.parentNode.lang;
window.decimalPoint = lang === 'en' ? '.' : ',';
window.thousandsSeparator = lang === 'en' ? ',' : '.';

window.dataChanged = false;
window.dateChangedMessage = '';

/*
 |--------------------------------------------------------------------------
 | Google maps load button
 |--------------------------------------------------------------------------
 */
onLoad(() => {
    if (typeof(initGoogleMaps) === 'function') {
        const button = document.querySelector('.map-load-button');
        button.on('click', function () {
            initGoogleMaps();
            showMapArea();
            hideMapButtonContainer();
        });

        function hideMapButtonContainer() {
            const mapButton = document.querySelector('.map-load-button-container');
            if (mapButton) {
                mapButton.style.display = 'none';
            }
        }

        function showMapArea() {
            const applet = document.querySelector('.map-applet');
            if (applet) {
                applet.style.display = 'block';
            }
        }
    }
});


/*
 |--------------------------------------------------------------------------
 | Language select
 |--------------------------------------------------------------------------
 */

scope(() => {
    // @todo: replace jq
    //variables
    const $flagCurrent = $('.language-select .current'),
             $flagMenu = $('.language-select .dropdown');

    //functions
    function onBtnClick() {
        $flagMenu.slideToggle(200);
    }

    //ready
    onLoad(() => {
        $flagCurrent.on('click', onBtnClick);
        $flagCurrent.on('mousedown', disableFastClick);
    });

});

/*
 |--------------------------------------------------------------------------
 | Miscellaneous
 |--------------------------------------------------------------------------
 */

onLoad(() => {
    const chartContainer = document.querySelector('.chart-container');
    if (chartContainer) {
        chartContainer.on('mousedown', disableFastClick);
    }

    window.addEventListener('contactPageOpened', e => {
        // Set the page title for the contact page
        e.detail.container.querySelector('input[name="page_name"]').setAttribute(
            'value',
            document.title.replace(' - heerlijkehuisjes.nl backoffice', '')
        );
    });
});

// Fixes numbers to be in price format.
export function fixNumbers(value) {
    let decimals = null;

    if (value.indexOf('.') !== -1 || value.indexOf(',') !== -1) {
        value = value.split(/[.,]/);

        let endingDigits = value.pop();

        value = value.join('');

        if (endingDigits.length > 2) {
            decimals = endingDigits;
        } else {
            if (endingDigits.length < 2) {
                endingDigits.padEnd(2, '0');
            }

            decimals = endingDigits;
        }
    }

    value = applyThousandsSeparator(value);

    if (decimals) {
        value = value + ',' + decimals.padEnd(2, '0');
    } else {
        value = value + ',00';
    }

    return value;
}

// Applies thousands separators to the given value.
function applyThousandsSeparator(value) {
    const parts = [];

    while (value.length > 3) {
        parts.push('.' + value.substring(value.length - 3));

        value = value.substring(0, value.length - 3);
    }

    return value + parts.reverse().join('');
}

/**
 * Returns whether the attribute value is true, false, or neither.
 * @param {string} value
 * @returns {undefined|boolean}
 */
export function getBoolFromAttribute(value) {
    if (value === '1' || value === 'true' || value === 'on') return true;
    if (value === '0' || value === 'false' || value === 'off') return false;
    return undefined;
}

/**
 * Returns whether the given HTML element is currently within the viewport.
 * @param {HTMLElement} element
 * @return {boolean}
 */
export function isWithinViewport(element) {
    const rect = element.getBoundingClientRect();

    return (rect.left > 0 && rect.right < window.innerWidth) && (rect.top > 0 && rect.bottom < window.innerHeight);
}

/**
 * A collection of helpful utility methods.
 */
export let Utils = {
    /**
     * Creates a clone from a template by its ID.
     * @param {string} id
     * @return {HTMLElement|Node}
     */
    getFragment: id => document.querySelector('#' + id)?.content?.firstElementChild.cloneNode(true),

    /**
     * Creates a new HTML fragment in which the given HTML is presented.
     * @param {string} html
     * @return {DocumentFragment}
     */
    createNode: html => document.createRange().createContextualFragment(html),

    /**
     * Returns whether the page is currently in 'dark-mode'.
     * @return {boolean}
     */
    isDarkMode: () => document.body.parentElement.classList.contains('dark'),

    /**
     * Attempts to fetch a translation based on the given path.
     * @param {string} key
     * @param {string|null} [language]
     * @param {boolean} [capitalize]
     * @return {*}
     */
    translate: function(key, language = null, capitalize = false) {
        if (! window.translations || ! window.translations[language || lang]) return key;
        let scope = window.translations[language || lang];
        key.split('.').every(chunk => scope = scope[chunk]);

        if (typeof(scope) === 'string' && capitalize) {
            return scope.substring(0, 1).toUpperCase() + scope.substring(1);
        }

        return scope || key;
    },

    /**
     * A list of supported locales and their proper language codes.
     */
    locales: {
        nl: 'nl-NL',
        de: 'de-DE',
        en: 'en-GB',
    },
    /**
     * A number formatter for Currency in whichever language the page is running in (default NL).
     * @type {Intl.NumberFormat}
     * @private
     */
    _formatter: null,

    /**
     * Formats the given number, as currency (in the current language's format).
     * @param {number} input
     * @return {string}
     */
    formatCurrency: function (input) {
        if (! Utils._formatter) {
            Utils._formatter = new Intl.NumberFormat('nl-NL', {
                style: 'currency',
                currency: 'EUR'
            });
        }

        return Utils._formatter.format(input);
    },

    /**
     * Runs the callback on the next rendered frame (the frame after this one).
     * @param {Function} callback
     */
    onNextFrame: function (callback) {
        requestAnimationFrame(() => requestAnimationFrame(callback));
    },

    /**
     * Returns a promise that gets resolved after this frame is done rendering.
     * @return {Promise<void>}
     */
    awaitNextFrame: function () {
        return new Promise(resolve =>
            Utils.onNextFrame(resolve)
        );
    },
};

window.Utils = Utils;

// Clipboard
onLoad(() => {
    // const elements = document.querySelectorAll('[data-clipboard-target]');

    // new Clipboard(elements);

    document.body.on('click', '.copyable > i.copy-value', e => {
        e.preventDefault();

        const value = e.currentTarget.dataset.value,
            message = e.currentTarget.dataset.message;

        if (value) {
            if (typeof(Clipboard) !== 'undefined') {
                navigator.clipboard
                    .writeText(value)
                    .then(() => {
                        if (isModuleLoaded('flash-messages')) {
                            new Snackbar({ message: message || 'Waarde gekopieerd.' });
                        }
                    });
            } else {
                if (isModuleLoaded('flash-messages')) {
                    new Snackbar({ message: 'Clipboard werkt alleen op HTTPS', class: 'error'});
                }
            }
        }

        return false;
    });
});

scope(() => {
    let lastScroll = 0;
    on('scroll', event => {
        const scrollTop = document.scrollingElement.scrollTop;

        const target = document.querySelector('.tabs-holder');
        if (target) {
            const rect = target.getBoundingClientRect();
            target.classList.toggle('stick', rect.top <= 0);

            target.classList.toggle('hide', scrollTop > lastScroll);

            document.body.style.setProperty('--header-size', (rect.top <= 0 && scrollTop <= lastScroll ? rect.height : '-1') + 'px');
        }

        lastScroll = scrollTop;
    });
})

/**
 * Profile popups
 */
onLoad(() => {
    let showTimeout,
        hideTimeout,
        visibleFor;

    const container = Utils.createNode('<div class="profile-preview"></div>').firstElementChild,
        cachedUsers = [];

    document.body.append(container);

    document.body.on('mouseover', '.profile-popup', e => {
            clearTimeout(showTimeout);
            clearTimeout(hideTimeout);

            const userId = e.currentTarget.dataset.userId;
            if (! userId) {
                return;
            }

            if (visibleFor !== userId) {
                showTimeout = setTimeout(() => {
                    fetchUser(userId, e);
                }, 1000);
            }
        })
        .on('mouseout', '.profile-popup', () => {
            clearTimeout(showTimeout);
            clearTimeout(hideTimeout);

            hideTimeout = setTimeout(hideBox, 250);
        })
        .on('wheel', () => hideBox());

    container
        .on('mouseover', () => {
            clearTimeout(showTimeout);
            clearTimeout(hideTimeout);
        })
        .on('mouseout', () => {
            clearTimeout(showTimeout);
            clearTimeout(hideTimeout);

            hideTimeout = setTimeout(hideBox, 250);
        });

    function fetchUser(userId, e) {
        visibleFor = userId;

        if (cachedUsers[userId]) {
            showBox(cachedUsers[userId], e.currentTarget);
        }
        else {
            new NetworkRequest('/fetch/users/' + userId, {quiet: true})
                .get()
                .then(result => {
                    if (result.body.status) {
                        console.log("cached user " + userId);
                        cachedUsers[userId] = result.body.data;

                        if (visibleFor === userId) {
                            showBox(result.body.data, e.currentTarget);
                        }
                    } else {
                        // user probably couldn't be found.
                        hideBox();
                    }
                });
        }
    }

    function showBox(content, parentElement) {
        // move the container to the target position
        const box = parentElement.getBoundingClientRect();

        container.innerHTML = content;
        container.style.display = 'block';
        container.style.left = (box.left + box.width - 8) + 'px';
        container.style.top = (box.top + box.height - 8) + 'px';

        requestAnimationFrame(() => requestAnimationFrame(() => {
            // 1 frame later...
            const box = container.getBoundingClientRect();

            if (box.bottom > window.innerHeight) {
                container.style.top = (window.innerHeight - box.height) + 'px';
            }
            if (box.right > window.innerWidth) {
                container.style.left = (window.innerWidth - box.width) + 'px';
            }
        }));
    }

    function hideBox() {
        visibleFor = null;
        container.style.display = 'none';
    }
});

/**
 * @type {[{indicator: LinearIndicator|CircularIndicator, parent: HTMLElement}]}
 */
window.progressIndicators = window.progressIndicators || [];

/**
 * Returns a progress indicator by their ID.
 *
 * @param {string} id
 * @returns {null|{indicator: LinearIndicator|CircularIndicator, parent: HTMLElement}}
 */
export function getIndicatorById(id) {
    const indicator = progressIndicators.find(i => i.indicator.element.id === id);

    return indicator ? indicator.indicator : null;
}
